var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, LitElement } from 'lit';
import { i18n } from '@lingui/core';
import { FormControlMixin } from '@open-wc/form-control';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import '../radio/radio.js';
import { activateI18n } from '../i18n';
import { messages as daMessages } from './locales/da/messages.mjs';
import { messages as enMessages } from './locales/en/messages.mjs';
import { messages as fiMessages } from './locales/fi/messages.mjs';
import { messages as nbMessages } from './locales/nb/messages.mjs';
import { messages as svMessages } from './locales/sv/messages.mjs';
// eslint-disable-next-line
// @ts-ignore
import { styles } from './radio-group-styles.js';
import { styles as hostStyles } from '../radio/host-styles.js';
activateI18n(enMessages, nbMessages, fiMessages, daMessages, svMessages);
const REQUIRED_MESSAGE = () => i18n._({
    id: 'radio-group.validation.required',
    message: 'Please select an option.',
    comment: 'Shown when required radio group has no selections',
});
/**
 * @slot label - Alternative to the `label` attribute should you need custom HTML.
 * @slot help-text - Alternative to the `help-text` attribute should you need custom HTML.
 */
export class WRadioGroup extends FormControlMixin(LitElement) {
    static { this.styles = [hostStyles, styles]; }
    //
    // We need this because if we don't have it, FormValidation yells at us that it's "not focusable".
    //   If we use `this.tabIndex = -1` we can't focus the radio inside.
    //
    static { this.shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true }; }
    constructor() {
        super();
        this.hasInteracted = false;
        this.hasWarnedMissingName = false;
        this.autoTabIndex = false;
        /**
         * The radio group's label. Required for proper accessibility. If you need to display HTML, use the `label` slot
         * instead.
         */
        this.label = '';
        /** The radio group's help text. If you need to display HTML, use the `help-text` slot instead. */
        this.helpText = '';
        /** Optional indicator for the label. */
        this.optional = false;
        /** Marks the radio group as invalid. */
        this.invalid = false;
        /** The name of the radio group */
        this.name = null;
        /** Disables the radio group and all child radios. */
        this.disabled = false;
        /** Ensures a child radio is checked before allowing the containing form to submit. */
        this.required = false;
        this.defaultCheckedValue = undefined;
        this.slottedHelpText = null;
        this.nameManagedRadios = new WeakSet();
        this.disabledManagedRadios = new WeakSet();
        this.handleRadioClick = (e) => {
            const clickedRadio = e.target.closest('w-radio');
            if (!clickedRadio || clickedRadio.disabled || this.disabled) {
                return;
            }
            const oldValue = this.getCheckedValue();
            const radios = this.getAllRadios();
            this.selectSingleRadio(clickedRadio, radios);
            const newValue = this.getCheckedValue();
            if (newValue !== oldValue) {
                this.updateComplete.then(this.emitSelectionChange);
            }
        };
        this.handleInvalid = (event) => {
            // Prevent native validation popover. We surface errors via help text.
            event.preventDefault();
            this.hasInteracted = true;
            this.updateValidity();
        };
        this.handleHelpTextSlotChange = () => {
            this.syncSlottedHelpText();
            this.requestUpdate();
        };
        this.handleI18nChange = () => {
            this.requestUpdate();
        };
        this.emitSelectionChange = () => {
            this.hasInteracted = true;
            this.syncFormValue();
            this.updateValidity();
            // Re-render derived UI (help text/error styles) that depends on current checked radio.
            this.requestUpdate();
            this.dispatchEvent(new InputEvent('input', { bubbles: true, composed: true }));
            this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
        };
        this.captureDefaultSelection = () => {
            if (this.defaultCheckedValue === undefined) {
                this.defaultCheckedValue = this.getCheckedValue();
            }
        };
        this.addEventListener('keydown', this.handleKeyDown);
        this.addEventListener('click', this.handleRadioClick);
        this.addEventListener('invalid', this.handleInvalid);
    }
    /**
     * We use the first available radio as the validationTarget similar to native HTML that shows the validation popup on
     * the first radio element.
     */
    get validationTarget() {
        const radio = this.querySelector(':is(w-radio):not([disabled])');
        if (!radio)
            return undefined;
        return radio;
    }
    updated(changedProperties) {
        if (changedProperties.has('disabled') ||
            changedProperties.has('name') ||
            changedProperties.has('required') ||
            changedProperties.has('invalid') ||
            changedProperties.has('helpText')) {
            this.syncRadioElements();
            this.syncFormValue();
            this.updateValidity();
        }
    }
    connectedCallback() {
        super.connectedCallback();
        this.syncSlottedHelpText();
        this.syncFormValue();
        this.updateValidity();
        this.unsubscribeI18n = i18n.on('change', this.handleI18nChange);
        this.warnIfMissingName();
        this.updateComplete.then(this.captureDefaultSelection);
    }
    disconnectedCallback() {
        this.unsubscribeI18n?.();
        this.unsubscribeI18n = undefined;
        super.disconnectedCallback();
    }
    resetFormControl() {
        const defaultValue = this.defaultCheckedValue ?? null;
        this.getAllRadios().forEach((radio) => {
            radio.checked = defaultValue ? radio.value === defaultValue : false;
        });
        this.syncRadioElements();
        this.syncFormValue();
        this.updateValidity();
    }
    getAllRadios() {
        return [...this.querySelectorAll('w-radio')];
    }
    getCheckedValue() {
        const checked = this.getAllRadios().find((radio) => radio.checked);
        return checked?.value ?? null;
    }
    getEnabledRadios(radios = this.getAllRadios()) {
        return radios.filter((radio) => !radio.disabled);
    }
    selectSingleRadio(selected, radios = this.getAllRadios()) {
        radios.forEach((radio) => {
            const isSelected = radio === selected;
            radio.checked = isSelected;
            radio.setAttribute('tabindex', isSelected ? '0' : '-1');
        });
    }
    handleLabelClick() {
        this.focus();
    }
    async syncRadioElements() {
        const radios = this.getAllRadios();
        // Add data attributes to support styling
        radios.forEach((radio, index) => {
            radio.toggleAttribute('data-w-radio-first', index === 0);
            radio.toggleAttribute('data-w-radio-inner', index !== 0 && index !== radios.length - 1);
            radio.toggleAttribute('data-w-radio-last', index === radios.length - 1);
            this.syncRadioDisabledState(radio);
            if (this.name) {
                if (!radio.getAttribute('name') || this.nameManagedRadios.has(radio)) {
                    radio.setAttribute('name', this.name);
                    this.nameManagedRadios.add(radio);
                }
            }
            else if (this.nameManagedRadios.has(radio)) {
                radio.removeAttribute('name');
                this.nameManagedRadios.delete(radio);
            }
        });
        await Promise.all(radios.map(async (radio) => radio.updateComplete));
        this.normalizeCheckedRadios(radios);
        this.syncTabOrder(radios);
    }
    syncRadioDisabledState(radio) {
        if (this.disabled) {
            if (!radio.disabled) {
                radio.disabled = true;
                this.disabledManagedRadios.add(radio);
            }
            return;
        }
        if (this.disabledManagedRadios.has(radio)) {
            radio.disabled = false;
            this.disabledManagedRadios.delete(radio);
        }
    }
    syncTabOrder(radios) {
        if (this.disabled) {
            radios.forEach((radio) => {
                radio.tabIndex = -1;
            });
            return;
        }
        const enabledRadios = this.getEnabledRadios(radios);
        const checkedRadio = enabledRadios.find((radio) => radio.checked);
        if (enabledRadios.length > 0) {
            if (checkedRadio) {
                enabledRadios.forEach((radio) => {
                    radio.tabIndex = radio.checked ? 0 : -1;
                });
            }
            else {
                enabledRadios.forEach((radio, index) => {
                    radio.tabIndex = index === 0 ? 0 : -1;
                });
            }
        }
        radios
            .filter((radio) => radio.disabled)
            .forEach((radio) => {
            radio.tabIndex = -1;
        });
    }
    handleKeyDown(event) {
        if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' '].includes(event.key) || this.disabled) {
            return;
        }
        const allRadios = this.getAllRadios();
        const radios = this.getEnabledRadios(allRadios);
        if (radios.length <= 0) {
            return;
        }
        event.preventDefault();
        const oldValue = this.getCheckedValue();
        const checkedRadio = radios.find((radio) => radio.checked) ?? radios[0];
        const incr = event.key === ' ' ? 0 : ['ArrowUp', 'ArrowLeft'].includes(event.key) ? -1 : 1;
        let index = radios.indexOf(checkedRadio) + incr;
        if (index < 0) {
            index = radios.length - 1;
        }
        if (index >= radios.length) {
            index = 0;
        }
        this.selectSingleRadio(radios[index], allRadios);
        radios[index].focus();
        const newValue = this.getCheckedValue();
        if (newValue !== oldValue) {
            this.updateComplete.then(this.emitSelectionChange);
        }
    }
    /** Sets focus on the radio group. */
    focus(options) {
        if (this.disabled)
            return;
        const radios = this.getAllRadios();
        const checked = radios.find((radio) => radio.checked);
        const firstEnabledRadio = radios.find((radio) => !radio.disabled);
        const radioToFocus = checked || firstEnabledRadio;
        // Call focus for the checked radio. If no radio is checked, focus the first one that isn't disabled.
        if (radioToFocus) {
            radioToFocus.focus(options);
        }
    }
    checkValidity() {
        this.updateValidity();
        return this.internals.checkValidity();
    }
    reportValidity() {
        this.hasInteracted = true;
        this.updateValidity();
        return this.internals.checkValidity();
    }
    hasSlottedContent(name) {
        if (this.querySelector(`[slot="${name}"]`)) {
            return true;
        }
        const slot = this.shadowRoot?.querySelector(`slot[name="${name}"]`);
        if (!slot)
            return false;
        return slot.assignedNodes({ flatten: true }).some((node) => {
            if (node.nodeType === Node.ELEMENT_NODE)
                return true;
            if (node.nodeType === Node.TEXT_NODE)
                return Boolean(node.textContent?.trim());
            return false;
        });
    }
    syncFormValue() {
        // Radio groups should not contribute values to form data. Radios submit their own values.
        this.setValue(null);
    }
    syncSlottedHelpText() {
        const slottedHelpText = this.querySelector('[slot="help-text"]');
        if (!slottedHelpText) {
            this.slottedHelpText = null;
            return;
        }
        this.slottedHelpText = slottedHelpText.textContent?.trim() || null;
    }
    updateValidity() {
        this.warnIfMissingName();
        if (this.disabled) {
            this.internals.setValidity({});
            this.syncChildInvalid(false);
            this.syncHostTabIndex(false);
            return;
        }
        const requiredInvalid = this.required && !this.getCheckedValue();
        const externalInvalid = this.invalid;
        const showRequiredError = requiredInvalid && this.hasInteracted;
        const showInvalidUi = externalInvalid || showRequiredError;
        this.syncHostTabIndex(showInvalidUi);
        if (requiredInvalid) {
            this.setValidityState({ valueMissing: true });
            this.syncChildInvalid(showInvalidUi);
            return;
        }
        if (externalInvalid) {
            this.setValidityState({ customError: true });
            this.syncChildInvalid(true);
            return;
        }
        this.internals.setValidity({});
        this.syncChildInvalid(false);
    }
    normalizeCheckedRadios(radios) {
        const checkedRadio = radios.find((radio) => radio.checked);
        if (!checkedRadio)
            return;
        radios.forEach((radio) => {
            if (radio !== checkedRadio) {
                radio.checked = false;
            }
        });
    }
    syncChildInvalid(isInvalid) {
        this.getAllRadios().forEach((radio) => {
            radio.invalid = isInvalid;
        });
    }
    setValidityState(state) {
        const anchor = this.validationTarget;
        this.internals.setValidity(state, ' ', anchor ?? undefined);
    }
    syncHostTabIndex(shouldBeFocusable) {
        const hasTabIndexAttr = this.hasAttribute('tabindex');
        // If the host already has a tabindex attribute, we won't override it with autoTabIndex logic.
        // This allows users to set tabindex="-1" on the host to remove the radio group's presence from the tab order if they want.
        if (hasTabIndexAttr && !this.autoTabIndex)
            return;
        // If the radio group is invalid, it should be focusable to show the validation error. If it's valid, it should not be focusable to avoid confusion with the radios inside.
        if (shouldBeFocusable) {
            this.tabIndex = 0;
            this.autoTabIndex = true;
            return;
        }
        // Only remove the tabindex if we added it. If the user set a tabindex attribute, we should leave it alone.
        if (this.autoTabIndex) {
            // We use removeAttribute here instead of setting tabIndex to undefined or null because the latter will not remove the tabindex from the DOM, it will just set it to "null" or "undefined" which is still focusable.
            this.removeAttribute('tabindex');
            this.autoTabIndex = false;
        }
    }
    warnIfMissingName() {
        if (this.hasWarnedMissingName)
            return;
        if (!this.internals.form)
            return;
        if (this.name && this.name.trim().length > 0)
            return;
        console.warn('w-radio-group: "name" is required for form submission.');
        this.hasWarnedMissingName = true;
    }
    render() {
        const hasLabelSlot = this.hasSlottedContent('label');
        const hasHelpTextSlot = this.hasSlottedContent('help-text');
        const hasLabel = this.label ? true : !!hasLabelSlot;
        const hasHelpText = this.helpText ? true : !!hasHelpTextSlot;
        const isRequiredInvalid = this.required && !this.getCheckedValue();
        const showRequiredError = isRequiredInvalid && this.hasInteracted;
        const showInvalidError = this.invalid || showRequiredError;
        const isInvalid = showInvalidError;
        const helpText = showInvalidError ? REQUIRED_MESSAGE() : this.helpText;
        const shouldShowHelpText = showInvalidError || hasHelpText;
        const labelledBy = hasLabel ? 'label' : undefined;
        const describedBy = shouldShowHelpText ? 'help-text' : undefined;
        const helpTextAriaLabel = this.slottedHelpText || undefined;
        return html `
      <fieldset
        part="form-control"
        class=${classMap({
            'form-control': true,
            'form-control-radio-group': true,
            'form-control-has-label': hasLabel,
            'radio-group-required': this.required,
        })}
        role="radiogroup"
        aria-labelledby=${ifDefined(labelledBy)}
        aria-describedby=${ifDefined(describedBy)}
        aria-errormessage="error-message"
        aria-invalid=${isInvalid ? 'true' : undefined}>
        <label
          part="form-control-label"
          id="label"
          class="label"
          aria-hidden=${hasLabel ? 'false' : 'true'}
          @click=${this.handleLabelClick}>
          <slot name="label">${this.label}</slot>
          ${this.optional
            ? html ` <span class="optional">
                ${i18n._({
                id: 'radio-group.label.optional',
                message: 'Optional',
                comment: 'Shown behind label when marked as optional',
            })}
              </span>`
            : null}
        </label>

        <slot part="form-control-input" @slotchange=${this.syncRadioElements}></slot>

        ${shouldShowHelpText
            ? html `
              <div
                id="help-text"
                part="help-text"
                class=${classMap({
                'has-slotted': hasHelpText,
                error: isInvalid,
            })}
                aria-hidden=${shouldShowHelpText ? 'false' : 'true'}
                aria-label=${helpTextAriaLabel}>
                <slot name="help-text" @slotchange=${this.handleHelpTextSlotChange}>${helpText}</slot>
              </div>
            `
            : null}
      </fieldset>
    `;
    }
}
__decorate([
    state()
], WRadioGroup.prototype, "hasInteracted", void 0);
__decorate([
    property()
], WRadioGroup.prototype, "label", void 0);
__decorate([
    property({ attribute: 'help-text' })
], WRadioGroup.prototype, "helpText", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WRadioGroup.prototype, "optional", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WRadioGroup.prototype, "invalid", void 0);
__decorate([
    property({ reflect: true })
], WRadioGroup.prototype, "name", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WRadioGroup.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WRadioGroup.prototype, "required", void 0);
if (!customElements.get('w-radio-group')) {
    customElements.define('w-radio-group', WRadioGroup);
}
//# sourceMappingURL=radio-group.js.map